import { z } from 'zod';

export type MediaType = 'movie' | 'series';
export const mediaTypeSchema = z.enum(['movie', 'series']);

export const mediaIdSchema = z
  .string()
  .regex(/^[a-z]+-[A-Za-z0-9_]+$/, 'Invalid media id')
  .max(64);

export interface Rating {
  source: 'imdb' | 'tmdb' | 'demo';
  value: number;
  scale: 10;
  votes?: number;
}

export interface MediaSummary {
  id: string;
  type: MediaType;
  title: string;
  year: number | null;
  overview: string | null;
  /** Original language (ISO 639-1), where the list it came from says. */
  language?: string | null;
  posterUrl: string | null;
  backdropUrl: string | null;
  rating: Rating | null;
  genres: string[];
  certification: string | null;
  /** Release (film) or first air (series) date, when known: tells "Coming Soon" apart. */
  releaseDate?: string | null;
}

export interface CastMember {
  id: string;
  name: string;
  character: string | null;
  profileUrl: string | null;
}

export interface CrewMember {
  id: string;
  name: string;
  job: string;
}

export interface Trailer {
  key: string;
  site: 'YouTube';
  name: string;
}

interface DetailsBase extends MediaSummary {
  originalTitle: string | null;
  tagline: string | null;
  cast: CastMember[];
  productionCompanies: string[];
  countries: string[];
  languages: string[];
  /** The primary (original) language's ISO 639-1 code, such as "en" or "ko". */
  originalLanguage?: string | null;
  trailers: Trailer[];
  imdbId: string | null;
  recommendations: MediaSummary[];
  similar: MediaSummary[];
}

export interface MovieDetails extends DetailsBase {
  type: 'movie';
  runtime: number | null;
  releaseDate: string | null;
  directors: CrewMember[];
  writers: CrewMember[];
}

export interface SeasonSummary {
  seasonNumber: number;
  name: string;
  episodeCount: number;
  posterUrl: string | null;
  airDate: string | null;
}

export interface SeriesDetails extends DetailsBase {
  type: 'series';
  firstAirDate: string | null;
  lastAirDate: string | null;
  status: string | null;
  numberOfSeasons: number;
  numberOfEpisodes: number;
  episodeRuntime: number | null;
  creators: CrewMember[];
  seasons: SeasonSummary[];
}

export interface Episode {
  id: string;
  seasonNumber: number;
  episodeNumber: number;
  title: string;
  overview: string | null;
  stillUrl: string | null;
  runtime: number | null;
  airDate: string | null;
}

export interface SeasonDetails {
  seasonNumber: number;
  name: string;
  overview: string | null;
  episodes: Episode[];
}

export interface PersonSummary {
  id: string;
  name: string;
  department: string | null;
  profileUrl: string | null;
  knownFor: string[];
}

export interface ContentRow {
  id: string;
  title: string;
  subtitle?: string;
  items: MediaSummary[];
  more?: RowSource;
}

export interface RowSource {
  kind: 'trending' | 'popular' | 'top_rated' | 'new_releases' | 'hidden_gems';
  type: 'movie' | 'series' | 'all';
  genres?: { movie?: string; series?: string };
}

export interface ProviderInfo {
  provider: string;
  live: boolean;
  attribution: string | null;
}

export interface HomeFeed {
  featured: MediaSummary[];
  rows: ContentRow[];
  source: ProviderInfo;
}

export interface Genre {
  id: string;
  name: string;
}

export interface WelcomeSlides {
  slides: { id: string; title: string; year: number | null; backdropUrl: string }[];
}

export interface SearchResults {
  query: string;
  movies: MediaSummary[];
  series: MediaSummary[];
  people: PersonSummary[];
  page: number;
  totalPages: number;
  source: ProviderInfo;
  /** Set for a "films like X" search: the title the results are like. */
  like?: MediaSummary | null;
  /** The tags the search was narrowed by (genre:Comedy, rated:7...). */
  filters?: SearchFilter[];
}

export interface SearchFilter {
  /** The tag as it reads, such as "genre:Comedy". */
  tag: string;
  /** In words, such as "Comedy" or "Rated 7+". */
  label: string;
  /** False when nothing matched it (genre:Nonsense), so it was left out. */
  ok: boolean;
}

export const discoverQuerySchema = z.object({
  type: mediaTypeSchema.default('movie'),
  genre: z.string().max(20).optional(),
  yearFrom: z.coerce.number().int().min(1870).max(2100).optional(),
  yearTo: z.coerce.number().int().min(1870).max(2100).optional(),
  minRating: z.coerce.number().min(0).max(10).optional(),
  language: z.string().max(8).optional(),
  certification: z.string().max(10).optional(),
  sort: z.enum(['popular', 'rating', 'newest', 'oldest', 'title']).default('popular'),
  page: z.coerce.number().int().min(1).max(500).default(1),
});
export type DiscoverQuery = z.infer<typeof discoverQuerySchema>;

export interface PagedMedia {
  items: MediaSummary[];
  page: number;
  totalPages: number;
  source: ProviderInfo;
}

export const searchQuerySchema = z.object({
  q: z.string().trim().min(1).max(100),
  type: z.enum(['all', 'movie', 'series', 'person']).default('all'),
  page: z.coerce.number().int().min(1).max(100).default(1),
  record: z.enum(['true', 'false']).optional(),
});

/** Parts of an episode the player offers to skip, from the community timestamps database. */
export const SKIP_SEGMENT_KINDS = ['recap', 'intro', 'credits'] as const;
export type SkipSegmentKind = (typeof SKIP_SEGMENT_KINDS)[number];

/** Times in seconds from the start of the episode. */
export const skipSegmentSchema = z.object({
  kind: z.enum(SKIP_SEGMENT_KINDS),
  start: z.number().min(0),
  end: z.number().min(0),
});
export type SkipSegment = z.infer<typeof skipSegmentSchema>;

export interface EpisodeSegments {
  segments: SkipSegment[];
}
